import { useTranslation } from 'react-i18next';

import type { SectionNotice } from '@/lib/homeworkResubmit';
import { cn } from '@/lib/utils';

/**
 * Плашка пересдачи над заданиями раздела (договор homework-resubmit.md, решение
 * владельца 11.09.2026).
 *
 * ⚠️ ЗАЧЕМ. Куратор возвращал работу «на доработку», а в самом уроке об этом не
 * было ни слова: ученик видел свой старый ответ с «Сохранено» и не знал, что и
 * почему править. Комментарий жил только в «Домашке».
 *
 * ⚠️ КНОПКИ «СДАТЬ» ЗДЕСЬ НЕТ, И НЕ НУЖНО. Ученик исправляет ответ и сохраняет —
 * сервер сам возвращает работу в очередь (`pending`, `resubmitted: true`), а
 * очередь отправки сбрасывает список домашки (`main.tsx`), и плашка становится
 * «Отправлено на проверку заново».
 *
 * Комментарий куратора — текстом, не разметкой.
 */
export function SectionResubmitNotice({ notice }: { notice: SectionNotice }) {
  const { t } = useTranslation();

  if (notice.kind === 'resent') {
    return (
      <p
        data-resubmit-notice="resent"
        role="status"
        className="mt-4 flex items-center gap-2 rounded-[16px] bg-info-container px-4 py-2.5 text-[12.5px] font-semibold text-info"
      >
        <span className="material-symbols-outlined text-[16px]" aria-hidden="true">send</span>
        {t('lessonPlayer.resubmitSent')}
      </p>
    );
  }

  const rejected = notice.kind === 'rejected';
  return (
    <div
      data-resubmit-notice={notice.kind}
      role="status"
      className={cn(
        'mt-4 rounded-[16px] px-4 py-3 text-[12.5px]',
        rejected ? 'bg-error-container text-on-error-container' : 'bg-warning-container text-on-warning-container',
      )}
    >
      <p className="flex items-center gap-2 font-bold">
        {rejected ? (
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">block</span>
        ) : (
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">edit_note</span>
        )}
        {rejected ? t('lessonPlayer.resubmitRejected') : t('lessonPlayer.resubmitRevision')}
      </p>
      {notice.note && (
        <p className="mt-1.5 whitespace-pre-wrap text-on-surface">
          <span className="font-semibold">{t('lessonPlayer.resubmitCuratorNote')}</span> {notice.note}
        </p>
      )}
      <p className="mt-1.5">{t('lessonPlayer.resubmitHint')}</p>
    </div>
  );
}
